import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { validateAllFormFields } from 'src/app/core/functions';
import { ToastNotifierService } from 'src/app/core/services/toast-notifier.service';
import { ParticipantService } from '../../core/services/participant.service';
import { Participant, EDUCATION_LEVELS, QUALIFICATION_LEVELS } from 'src/app/core/model/participant';
import { requiredAndTrueValidator } from 'src/app/core/validators/required-and-true-validator';

@Component({
    selector: 'app-participant',
    templateUrl: './participant.component.html',
})
export class ParticipantComponent implements OnInit {
    saving = false;
    loading = true;
    submitted = false;
    hash: string;
    participant: Participant;
    fullName = '';
    educationLevels = EDUCATION_LEVELS;
    qualificationLevels = QUALIFICATION_LEVELS;

    months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

    participantForm = this.fb.group({
        phoneNumber: [''],
        email: ['', [Validators.required, Validators.email]],
        province: ['', Validators.required],
        dobDay: [null],
        dobMonth: [null],
        dobYear: [null],
        profession: [''],
        employer: ['', Validators.required],
        educationLevel: [null, Validators.required],
        educationLevelOther: [''],
        qualificationLevel: [null, Validators.required],
        participantConsent: [false, [Validators.required, requiredAndTrueValidator]],
        whatsappAccept: [false, [Validators.required, requiredAndTrueValidator]],
    });

    constructor(
        private participantService: ParticipantService,
        private fb: UntypedFormBuilder,
        private toastNotifierService: ToastNotifierService,
        private route: ActivatedRoute
    ) {
        this.hash = this.route.snapshot.params.hash;
    }

    ngOnInit() {
        this.loading = true;
        this.participantService.getParticipant(this.hash).subscribe({
            next: participant => {
                this.participant = participant;
                this.getFullName(participant);
                let dobDay = null, dobMonth = null, dobYear = null;
                if (participant.dateOfBirth) {
                    const parts = participant.dateOfBirth.split('-');
                    dobYear = parseInt(parts[0], 10);
                    dobMonth = parseInt(parts[1], 10);
                    dobDay = parseInt(parts[2], 10);
                }
                this.participantForm.patchValue({
                    phoneNumber: participant.phoneNumber,
                    email: participant.email,
                    province: participant.province,
                    dobDay,
                    dobMonth,
                    dobYear,
                    profession: participant.profession,
                    employer: participant.employer,
                    educationLevel: participant.educationLevel ?? null,
                    educationLevelOther: participant.educationLevelOther,
                    qualificationLevel: participant.qualificationLevel ?? null,
                });
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.toastNotifierService.fail('loadData');
            }
        });
    }

    getFullName(participant: Participant) {
        this.fullName = [participant.name, participant.surname1, participant.surname2]
            .filter(s => s?.trim())
            .join(' ');
    }

    get isOtherEducation(): boolean {
        return this.participantForm.get('educationLevel')?.value === 'other';
    }

    save() {
        this.saving = true;
        if (this.participantForm.valid) {
            const formValue = this.participantForm.value;
            this.participant = {
                ...this.participant,
                phoneNumber: formValue.phoneNumber,
                email: formValue.email,
                province: formValue.province,
                profession: formValue.profession,
                employer: formValue.employer,
                educationLevel: formValue.educationLevel,
                educationLevelOther: formValue.educationLevelOther,
                qualificationLevel: formValue.qualificationLevel,
            };
            if (formValue.dobDay && formValue.dobMonth && formValue.dobYear) {
                const d = formValue.dobDay.toString().padStart(2, '0');
                const m = formValue.dobMonth.toString().padStart(2, '0');
                this.participant.dateOfBirth = `${formValue.dobYear}-${m}-${d}`;
            }
            this.participantForm.disable();
            this.participantService.updateParticipant(this.hash, this.participant).subscribe({
                next: () => {
                    this.saving = false;
                    this.submitted = true;
                    this.toastNotifierService.success('formSubmittedSuccessfully');
                },
                error: () => {
                    this.saving = false;
                    this.participantForm.enable();
                    this.toastNotifierService.fail('sendData');
                }
            });
        } else {
            this.saving = false;
            validateAllFormFields(this.participantForm);
            this.toastNotifierService.fail('reviewfields');
        }
    }
}
